import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";
import { cn } from "@/lib/utils";
import { usePortalHost } from "../../providers/portal-host";
import {
  TOOLTIP_GAP_OFFSET,
  TOOLTIP_SIDE_OFFSET,
  tooltipArrowClass,
  tooltipBaseLabelClass,
  tooltipDescriptionClass,
  tooltipPopupClass,
  tooltipPositionerClass,
} from "./tooltip-styles";

export interface TooltipProps {
  /** The trigger element. Must be a single focusable React element. */
  children: React.ReactElement;
  /** Primary text shown in the tooltip */
  label: React.ReactNode;
  /** Optional secondary descriptive text */
  description?: React.ReactNode;
  /** Optional content aligned to the end of the primary label, such as a keyboard shortcut. */
  endSection?: React.ReactNode;
  /** Side of the trigger to show the tooltip */
  side?: "top" | "bottom" | "left" | "right";
  /** Alignment along the side */
  align?: "start" | "center" | "end";
  /** Prevents the tooltip from opening */
  disabled?: boolean;
  /** Whether to render the tooltip arrow */
  showArrow?: boolean;
}

/**
 * Wraps any focusable trigger element with a tooltip popup.
 *
 * @example
 * <Tooltip label="Settings">
 *   <IconButton icon={faGear} aria-label="Settings" />
 * </Tooltip>
 *
 * <Tooltip label="Required field" description="This field cannot be blank." side="right">
 *   <Input placeholder="Name" />
 * </Tooltip>
 */
function Tooltip({
  children,
  label,
  description,
  endSection,
  side = "top",
  align = "center",
  disabled = false,
  showArrow = true,
}: TooltipProps) {
  const portalHost = usePortalHost();

  return (
    <TooltipPrimitive.Root disabled={disabled}>
      <TooltipPrimitive.Trigger render={children} />
      <TooltipPrimitive.Portal container={portalHost}>
        <TooltipPrimitive.Positioner
          side={side}
          align={align}
          sideOffset={showArrow ? TOOLTIP_SIDE_OFFSET : TOOLTIP_GAP_OFFSET}
          className={tooltipPositionerClass}
        >
          <TooltipPrimitive.Popup className={tooltipPopupClass}>
            {showArrow && (
              <TooltipPrimitive.Arrow
                data-slot="tooltip-arrow"
                className={tooltipArrowClass}
              />
            )}
            <div className="flex items-center justify-between gap-(--spacing-8)">
              <p
                className={cn(
                  tooltipBaseLabelClass,
                  description
                    ? "font-(number:--font-weight-semibold)"
                    : "font-(number:--font-weight-regular)",
                )}
              >
                {label}
              </p>
              {endSection}
            </div>
            {description && (
              <p className={tooltipDescriptionClass}>{description}</p>
            )}
          </TooltipPrimitive.Popup>
        </TooltipPrimitive.Positioner>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

export { Tooltip };
